import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * AuthCallback — landing after the first-party Google OAuth roundtrip.
 *
 * Flow:
 *   1. User taps "Continue with Google"
 *   2. Backend /api/auth/google/start → browser goes to accounts.google.com
 *   3. Google → backend /api/auth/google/callback (session_token cookie set)
 *   4. Backend 303-redirects to /auth/callback?src=google_new (new user)
 *      OR straight to /app (returning user)
 *
 * On this page we:
 *   - Refresh the user context to hydrate AuthContext with the freshly
 *     cookie'd session.
 *   - If a fresh signup returned recovery codes via a short-lived
 *     `google_recovery_codes` cookie, surface them ONCE, then delete the
 *     cookie. This is the only channel where plaintext codes appear.
 *   - Route to /app (or /admin) once we have a user.
 */
function readCookie(name) {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[1]) : null;
}

function clearCookie(name) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax; Secure`;
}

export default function AuthCallback() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const { refresh } = useAuth();
    const [err, setErr] = useState(params.get("google_error"));
    const [codes, setCodes] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const raw = readCookie("google_recovery_codes");
                if (raw) {
                    try {
                        const parsed = JSON.parse(raw);
                        if (Array.isArray(parsed) && parsed.length > 0) setCodes(parsed);
                    } catch { /* ignore */ }
                    clearCookie("google_recovery_codes");
                }
                const me = await refresh();
                setBusy(false);
                if (!raw) {
                    // Returning user — no codes to show. Route immediately.
                    setTimeout(() => nav(me?.role === "admin" ? "/admin" : "/app"), 100);
                }
            } catch (e) {
                setBusy(false);
                setErr(e?.response?.data?.detail || "Google sign-in failed. Try again.");
            }
        })();
    }, [nav, refresh]);

    return (
        <div className="min-h-screen flex items-center justify-center p-8 text-center">
            <div className="max-w-md">
                {busy && !err && (
                    <>
                        <div className="w-11 h-11 mx-auto mb-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                        <div className="overline text-primary mb-2">Signing you in</div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.15] mb-2">
                            One <span className="text-primary">moment</span>.
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            Finalising your Google session…
                        </p>
                    </>
                )}

                {err && (
                    <>
                        <div className="overline text-primary mb-2">Sign-in error</div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.15] mb-2 text-danger">
                            Google sign-in failed
                        </h1>
                        <p className="text-sm text-muted-foreground mb-4">{err}</p>
                        <a className="text-primary underline text-sm" href="/login">← Back to login</a>
                    </>
                )}

                {!err && codes && (
                    <div data-testid="google-recovery-codes-view" className="text-left">
                        <div className="overline text-primary mb-2">Welcome to Melian Dialogue</div>
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.15] mb-3">
                            Save your <span className="text-primary">10 recovery codes</span>.
                        </h1>
                        <p className="text-sm text-muted-foreground mb-4">
                            This is the only time we&rsquo;ll show them. Save offline — they&rsquo;re
                            the only self-serve way back into your account if you ever lose Google access.
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-5 p-3 rounded-xl border border-dashed border-primary/30 bg-primary/[0.03]">
                            {codes.map((c, i) => (
                                <div key={c} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-background border border-border font-mono text-[13px] proportional-nums">
                                    <span className="text-[10px] font-bold text-muted-foreground shrink-0 w-5">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="font-bold truncate">{c}</span>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => nav("/app")}
                            className="w-full text-white font-extrabold px-6 py-3 rounded-lg bg-primary hover:bg-primary/90 transition-colors"
                            data-testid="google-recovery-continue"
                        >
                            I&rsquo;ve saved them — continue
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
